import {CounterLabel} from '@primer/react'
import {useId} from 'react'

import type {McpCustomization} from '../types/customization'
import {McpServerRow, type McpServerRowProps} from './McpServerRow'
import styles from './ConnectorSection.module.css'

export interface McpServerSectionProps extends Pick<McpServerRowProps, 'onEdit' | 'onDelete'> {
  title: string
  description?: string
  servers: McpCustomization[]
  /** Rendered in place of the list when `servers` is empty. */
  emptyState?: React.ReactNode
}

/**
 * A titled, counted list of the user's own MCP servers.
 *
 * Kept separate from `ConnectorSection` so connector-managed and direct
 * personal sources read distinctly. The shared
 * stylesheet keeps them visually consistent.
 */
export function McpServerSection({title, description, servers, onEdit, onDelete, emptyState}: McpServerSectionProps) {
  const headingId = useId()

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.header}>
        <h2 className={styles.title} id={headingId}>
          {title}
        </h2>
        <CounterLabel>{servers.length}</CounterLabel>
      </div>
      {description ? <p className={styles.description}>{description}</p> : null}
      {servers.length === 0 ? (
        emptyState
      ) : (
        <ul className={styles.list}>
          {servers.map(server => (
            <li key={server.id} className={styles.item}>
              <McpServerRow server={server} onEdit={onEdit} onDelete={onDelete} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
